<template lang="html">
  <div class="d-flex flex-column" id="Assessment">
    <div class="header d-flex pa-4">
      <button
        aria-role="button" aria-label="Go back to table of contents"
        class="back-to-toc pl-sm-4 d-flex align-center"
        @click="[processUserProgress(), $router.go(-1)]"
      >
        <img :src="BackIcon" />
        <div class="link-text pl-2 d-none d-lg-flex">Back</div>
      </button>
      <div class="header-content d-none d-sm-flex ml-sm-9 align-center">
        <v-tooltip bottom content-class="tooltip-arrow px-2 py-1">
          <template v-slot:activator="{ on, attrs }">
            <p class="mb-0" v-bind="attrs" v-on="on">
              <span class="assessment-card-type">
                {{ section }}
              </span>
              <span class="assessment-card-title ml-2">
                {{ chapterTitle }}
              </span>
            </p>
          </template>
          <span>{{ section }} {{ chapterTitle }}</span>
        </v-tooltip>
      </div>
      <div aria-role="progressbar" aria-label="Progress through your quiz" aria-valuemin="0" aria-valuemax="100"
        class="progress-container align-center d-flex d-sm-none">
        <v-progress-linear :value="progressValue" height="12" color="#5465FB" background-color="#EBF0F0"
          class="progress-bar"></v-progress-linear>
      </div>
      <div class="navigation-container ml-auto d-none d-sm-flex">
        <Navigation class="pa-0" :section="section" :question="total" :current="selected" ref="navComponent" />
      </div>
      <div aria-role="menu" aria-haspopup="true" class="menu-container d-flex align-center d-sm-none"
        @click="overlay = !overlay">
        <img :src="MenuIcon" />
      </div>
    </div>

    <div class="question-div">
      <div class="base-container">
        <v-window v-model="questionListSelected" @change="updateSelected">
          <v-window-item v-for="(q, i) in this.sectionContent.Question" :key="i" :eager=true>
            <div class="question-number">
              Question {{ i + 1 }} / {{ total }}
            </div>
            <BaseQuestion>
              <div class="base-content" v-if="q.$ === undefined" v-html="q.QuestionContent[0]">
              </div>
              <MultipleChoice v-else-if="q.$.type === 'multiple-choice'" :questionContent="q" :bookTitle="bookTitle"
                :chapter="chapter" :section="section" :key="selected" />
              <ExtendedResponse v-else-if="q.$.type === 'extended-response'" :questionContent="q" :bookTitle="bookTitle"
                :chapter="chapter" :section="section" />
            </BaseQuestion>
          </v-window-item>
        </v-window>
      </div>
    </div>

    <v-overlay :opacity="overlayOpacity" :value="overlay" v-resize="closeOverlayOnResize" :color="overlayColor">
      <v-container v-click-outside="closeOverlay" class="pa-0" id="action-container">
        <v-row dense>
          <v-col cols="12">
            <v-card class="nav-card rounded-lg d-flex flex-column pl-0" elevation="0" tag="ol">
              <v-card-subtitle class="question card-content pt-3 px-6 pb-6" tag="li">
                <div class="card-title">
                  <span v-html="section"></span>
                </div>
              </v-card-subtitle>
              <v-dialog v-model="dialog" attach="#action-container" transition="scroll-x-transition" fullscreen
                hide-overlay>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn aria-role="nav" tag="li" block class="action-btn d-flex justify-space-between py-3 px-6"
                    @click="dialog = true">
                    <p class="mb-0">Questions</p>
                    <img :src="RightIcon" />
                  </v-btn>
                </template>
                <v-card>
                  <div aria-role="button" aria-label="Close the menu"
                    class="d-flex align-center justify-space-between px-4 pt-4 pb-6">
                    <button class="d-flex align-center" @click="closeDialog">
                      <img :src="LeftIcon" />
                    </button>
                    <div class="question-title">
                      <span>Questions</span>
                    </div>
                    <div></div>
                  </div>
                  <v-list class="pa-0">
                    <v-list-item-group tag="ol" v-model="questionListSelected" class="d-flex flex-column pl-0">
                      <v-list-item v-for="n in total" :key="n" aria-role="link" :aria-label="`Go to question ${n}`"
                        tag="li" class="d-flex justify-space-between px-10 py-3" @click="updateCard(n)">
                        <span>Question {{ n }}</span>
                        <img :src="CheckIcon" v-if="selected == n" />
                      </v-list-item>
                    </v-list-item-group>
                  </v-list>
                </v-card>
              </v-dialog>
              <v-btn aria-role="button" aria-label="Show solution of current question" tag="li" block
                class="action-btn d-flex justify-space-between py-3 px-6" v-if="hasSolution"
                @click="toggleShowSolution">
                <p class="mb-0">{{ showSolution ? 'Hide' : 'Show' }} Solution</p>
              </v-btn>
            </v-card>
          </v-col>
          <v-col cols="12">
            <v-btn aria-role="button" aria-label="Close the menu" tag="li" elevation="0" block
              class="done-btn py-3 px-0" @click="closeOverlay">
              <p class="mb-0">
                Done
              </p>
            </v-btn>
          </v-col>
        </v-row>
      </v-container>
    </v-overlay>

    <div class="footer d-none d-sm-flex px-9 py-7 align-center justify-lg-end"
      :class="selected > 1 ? 'justify-space-between' : 'justify-end button-container'">
      <v-btn aria-role="button" aria-label="Go to previous question" class="previous d-none d-sm-flex px-7 py-2"
        :class="$vuetify.breakpoint.xl || $vuetify.breakpoint.lg ? 'mr-auto' : ''" v-if="selected > 1" @click="goLeft">
        <div class="previous-content d-flex align-center">
          <v-icon>mdi-chevron-left</v-icon>
          <div>Previous</div>
        </div>
      </v-btn>
      <v-btn aria-role="button" aria-label="Show solution to question" class="show-solution d-none d-sm-flex px-7 py-2"
        :class="$vuetify.breakpoint.xl || $vuetify.breakpoint.lg ? 'mr-8' : ''" v-if="hasSolution"
        @click="toggleShowSolution">
        <div class="show-solution-content d-flex align-center">
          <v-icon>mdi-eye</v-icon>
          <div>{{ showSolution ? 'Hide' : 'Show' }} Solution</div>
        </div>
      </v-btn>
      <v-btn aria-role="button" aria-label="Go to next question" class="next px-7 py-2" v-if="selected !== total"
        @click="goRight">
        <div class="next-content d-flex align-center">
          <div>Next</div>
          <v-icon>mdi-chevron-right</v-icon>
        </div>
      </v-btn>
    </div>
    <div class="footer d-xs-flex d-sm-none px-6 py-4 px-sm-9 py-sm-7 justify-space-between align-center">
      <v-btn aria-role="button" aria-label="Go to previous question" class="previous d-none d-sm-flex px-7 py-2"
        v-if="selected > 1" @click="goLeft">
        <div class="previous-content d-flex align-center">
          <v-icon>mdi-chevron-left</v-icon>
          <div>Previous</div>
        </div>
      </v-btn>
      <v-btn aria-role="button" aria-label="Show solution to question" class="show-solution d-none d-sm-flex px-7 py-2"
        v-if="hasSolution" @click="toggleShowSolution">
        <div class="show-solution-content d-flex align-center">
          <v-icon>mdi-eye</v-icon>
          <div>{{ showSolution ? 'Hide' : 'Show' }} Solution</div>
        </div>
      </v-btn>
      <v-btn aria-role="button" aria-label="Go to next question" class="next" v-if="selected !== total"
        @click="goRight">
        <div class="next-content d-flex align-center">
          <div>Next</div>
          <v-icon>mdi-chevron-right</v-icon>
        </div>
      </v-btn>
    </div>
  </div>
</template>

<script lang="js">
import MultipleChoice from '@/components/assessment/MultipleChoice'
import ExtendedResponse from '@/components/assessment/ExtendedResponse'
import BaseQuestion from '@/components/assessment/BaseQuestion'
import Navigation from '@/components/assessment/Navigation'

import BackIcon from '../../public/img/icons/back.svg'
import MenuIcon from '../../public/img/icons/menu.svg'
import RightIcon from '../../public/img/icons/right.svg'
import LeftIcon from '../../public/img/icons/left.svg'
import CheckIcon from '../../public/img/icons/check.svg'

import { mapState, mapActions, mapGetters } from 'vuex'

export default {
  name: 'Assessment',
  components: {
    BaseQuestion,
    MultipleChoice,
    ExtendedResponse,
    Navigation
  },
  props: ['chapter', 'section'],
  beforeMount () {
    this.initializeUserProgress()
  },
  mounted () {
    this.formatContents()
    this.addGTMEvent()
  },
  updated: function () {
    this.formatContents()
  },
  beforeDestroy: function () {
    this.setShowSolution(false)
    let tableLength = this.tableElements.length
    for (var i = 0; i < tableLength; i++) {
      this.tableElements[i].removeEventListener('touchstart', this.tableTouchstart)
    }
  },
  data: () => ({
    tableElements: undefined,
    overlay: false,
    overlayOpacity: 0.6,
    overlayColor: '#8E9399',
    dialog: false,
    BackIcon,
    MenuIcon,
    RightIcon,
    LeftIcon,
    CheckIcon,
    hasSolution: false
  }),
  methods: {
    ...mapActions({
      setProgress: 'user/setAssessmentProgress',
      setSelected: 'question/setSelected',
      setNav: 'question/setNav',
      setShowSolution: 'question/setShowSolution'
    }),
    closeOverlay () {
      this.overlay = false
    },
    closeOverlayOnResize () {
      const SM_SCREEN = 600
      if (window.innerWidth > SM_SCREEN) {
        this.closeOverlay()
      }
    },
    closeDialog () {
      this.dialog = false
    },
    updateCard (data) {
      this.closeOverlay()
      this.closeDialog()
      this.$refs.navComponent.selectQuestion(data)
      this.setShowSolution(false)
    },
    toggleShowSolution () {
      this.setShowSolution(!this.showSolution)
      this.closeOverlay()
    },
    async formatContents () {
      setImagesLightBox()
      setTablesSideScroll()
      await MathJax.typesetPromise()

      this.tableElements = this.$el.querySelectorAll('table')
      this.initTables()
      this.displaySolutionButton()
    },
    initializeUserProgress () {
      const assessment =
        this.progress.length &&
        this.progress.find(assessment => {
          return assessment.title === this.chapterTitle &&
            assessment.section === this.section
        })

      this.setSelected(assessment ? assessment.progress : 1)
    },
    processUserProgress () {
      this.setProgress({
        title: this.chapterTitle,
        section: this.section,
        progress: this.selected
      })
    },
    tableTouchstart (e) {
      e.stopPropagation()
    },
    initTables: function () {
      let tableLength = this.tableElements.length
      for (var i = 0; i < tableLength; i++) {
        this.tableElements[i].addEventListener('touchstart', this.tableTouchstart)
      }
    },
    displaySolutionButton () {
      this.$nextTick(function () {
        const activeItem = '.v-window-item--active'
        const questionSolution = '.question-solution'
        const element = document.querySelector(`${activeItem} ${questionSolution}`)
        this.hasSolution = !!element
      })
    },
    addGTMEvent () {
      let cfVideos = document.querySelectorAll('iframe')

      cfVideos.forEach(cfVideo => {
        let cfPlayer = Stream(cfVideo)
        let isOnLoop = false
        if (cfVideo.src.includes('loop=true')) {
          isOnLoop = true
        }

        cfPlayer.addEventListener('play', () => {
          this.$gtm.trackEvent({
            event: 'user_played_video',
            category: 'Assessment',
            action: 'play',
            label: 'User played video',
            productName: this.bookTitle,
            chapterName: this.chapter,
            sectionTitle: this.section,
            videoTitle: this.nowPlaying,
            isMediaPlayed: 'true',
            isVideoOnLoop: isOnLoop
          })
        })

        if (!isOnLoop) {
          cfPlayer.addEventListener('ended', () => {
            this.$gtm.trackEvent({
              event: 'user_finished_video',
              category: 'Assessment',
              action: 'completed',
              label: 'User finished watching video',
              productName: this.bookTitle,
              chapterName: this.chapter,
              sectionTitle: this.section,
              videoTitle: this.nowPlaying,
              isMediaFinished: 'true'
            })
          })
        }
      })
    },
    goLeft () {
      this.$refs.navComponent.goLeft()
    },
    goRight () {
      this.$refs.navComponent.goRight()
    },
    updateSelected (event) {
      this.closeOverlay()
      this.closeDialog()
      this.$refs.navComponent.selectQuestion(event + 1)
      this.setShowSolution(false)
    }
  },
  computed: {
    ...mapState({
      chapterTitle: state => state.book.chapterTitle,
      sectionContent: state => state.book.sectionContent,
      progress: state => state.user.assessments,
      bookTitle: state => state.book.bookTitle
    }),
    ...mapGetters({
      selected: 'question/getSelected',
      showSolution: 'question/getShowSolution'
    }),
    questionListSelected: {
      get () {
        return this.selected - 1
      },
      set (newValue) {
        return newValue
      }
    },
    total () {
      return this.sectionContent.Question.length
    },
    progressValue () {
      return Math.round((this.selected / this.total) * 100)
    }
  },
  watch: {
    selected () {
      document.documentElement.scrollTop = 0
    },
    showSolution (newValue) {
      if (newValue) {
        this.$gtm.trackEvent({
          event: 'user_clicked_show_solution',
          category: 'Assessment',
          action: 'click',
          label: 'User clicked show solution button',
          value: 'flip',
          productName: this.bookTitle,
          chapterName: this.chapter,
          sectionTitle: this.section,
          isSolutionClicked: 'true'
        })

        this.$nextTick(function () {
          const activeItem = '.v-window-item--active'
          const correctAnswer = '.solution-correct'
          const questionSolution = '.question-solution'
          let element = document.querySelector(`${activeItem} ${correctAnswer}`)

          if (!element) {
            element = document.querySelector(`${activeItem} ${questionSolution}`)
          }
          if (element) {
            const elementRect = element.getBoundingClientRect().top
            const y = elementRect + window.pageYOffset - 40
            window.scrollTo({ top: y, behavior: 'smooth' })
          }
        })
      }
    }
  }
}
</script>

<style lang="scss">
@import "@/assets/scss/variables.scss";

#Assessment {
  height: 100%;
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: $charcoal;

  .header {
    background-color: white;
    box-shadow: 0px 1px 4px rgba(0, 0, 0, 0.2);
    position: fixed;
    width: 100%;
    z-index: 1;

    .back-to-toc {
      color: #212529;
      .link-text {
        font-style: normal;
        font-weight: 700;
        font-size: 16px;
        line-height: 16px;
        letter-spacing: 0.05em;
      }
    }

    .header-content {
      display: flex;
      align-items: center;
      margin-left: 20px;
      p {
        display: -webkit-box;
        font-size: $PageTitle;
        line-height: 24px;
        word-break: break-word;
        overflow: hidden;
        text-overflow: ellipsis;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
      }
      .assessment-card-type {
        font-size: $PageTitle;
      }
      .assessment-card-title {
        font-size: $PageTitle;
      }
    }
  }

  .nav-card {
    border-color: white;
    background-color: white;
    list-style: none;

    .action-btn {
      background-color: white;
      border-top: 1px solid #DAE0E0;
      box-shadow: none;
      height: auto;

      p {
        color: $primary-navigation-text;
        font-size: 16px;
        font-weight: 400;
        line-height: 24px;
        letter-spacing: 1.5px;
        text-transform: none;
      }
    }

    .action-btn {
      border-bottom-left-radius: 8px;
      border-bottom-right-radius: 8px;
    }
  }

  .card-title {
    span {
      display: -webkit-box;
      max-height: 120px;
      font-size: 16px;
      color: $primary-navigation-text;
      font-weight: 700;
      line-height: 20px;
      text-overflow: ellipsis;
      word-wrap: break-word;
      overflow: hidden;
      -webkit-line-clamp: 5;
      -webkit-box-orient: vertical;
    }
  }

  .v-overlay__content {
    width: 100%;
    padding: 0 16px;
    top: 54px;
    position: fixed;
  }

  .done-btn {
    height: auto;
    border-radius: 8px;
    border-color: white;
    background-color: white;

    p {
      color: $primary-navigation-text;
      font-size: 16px;
      font-weight: 700;
      line-height: 24px;
      letter-spacing: 1.5px;
      text-transform: none;
    }
  }

  .base-container {
    margin: 86px auto 20px auto;
    width: 90%;
    padding-bottom: 92px;
    min-height: calc(100% - 178px);

    .question-number {
      width: 100%;
      font-weight: 700;
      font-size: 20px;
      line-height: 25px;
      letter-spacing: -2%;
      color: $gray;
      margin-top: 52px;
    }

    .v-window {
      .v-window-item {
        min-height: calc(75vh - 20px);
      }
    }
  }
}

.v-dialog {
  .v-list-item-group {
    list-style: none;

    .v-list-item {
      min-height: 0px;

      div {
        color: $primary-navigation-text;
        font-size: 16px;
        font-weight: 400;
        line-height: 20px;
        min-height: 0px;
      }
    }

    .v-list-item::after {
      content: none;
    }
  }

  .question-title {
    span {
      color: $primary-navigation-text;
      display: -webkit-box;
      font-size: 18px;
      font-weight: 700;
      line-height: 20px;
      max-height: 120px;
      overflow: hidden;
      text-overflow: ellipsis;
      word-wrap: break-word;
      -webkit-line-clamp: 5;
      -webkit-box-orient: vertical;
    }
  }

  .v-list-item--active {
    background-color: white;
    color: $primary-navigation-text;

    span {
      font-weight: 700;
      text-decoration: underline;
    }
  }

  .v-list-item:not(:first-child) {
    border-top: 1px solid #DAE0E0;
  }

  .v-list-item--active:before {
    background-color: transparent;
  }

  .theme--light.v-list-item:not(.v-list-item--active):not(.v-list-item--disabled) {
    color: $primary-navigation-text;
  }
}

.tooltip-arrow {
  left: 108px !important;
  top: 74px !important;
  max-width: 70% !important;
  background: #1F2127 !important;

  span {
    font-size: 18px;
    line-height: 28px;
  }
}

.tooltip-arrow::before {
  border-right: solid 4px transparent;
  border-left: solid 4px transparent;
  transform: translateX(-50%);
  position: absolute;
  z-index: 9;
  content: '';
  bottom: 100%;
  border-bottom: solid 6px #1F2127;
}

@mixin base-footer-button() {
  font-weight: 700;
  border-radius: 8px;
  letter-spacing: 1.25px;
  text-transform: none;
  box-shadow: none;

  &:focus-visible {
    outline: 4px solid $focus-button-outline;
    outline-offset: 4px;
  }
}

.footer {
  position: fixed;
  bottom: 0;
  width: 100%;
  z-index: 2;
  background-color: white;
  box-shadow: 0px 4px 16px rgba(0, 0, 0, 0.2);

  .previous {
    @include base-footer-button();
    color: $primary-navigation-text;
    border: none;
    background-color: white !important;

    .previous-content {
      gap: 4px;
    }

    &:focus::before {
      background-color: white !important;
    }

    &:hover {
      background-color: rgba(33, 37, 41, 0.09);
    }
  }
}

.button-container {
  gap: 32px;
}

.show-solution {
  @include base-footer-button();
  color: $primary-navigation-text;
  border: 2px solid $primary-navigation-text;
  background-color: white !important;

  .show-solution-content {
    gap: 4px;
  }

  &:focus::before {
    background-color: white !important;
  }

  &:hover {
    background-color: #DAE0E0;
  }
}

.next {
  @include base-footer-button();
  color: white !important;
  border: 2px solid $primary-navigation-text;
  background-color: $primary-button-background !important;
  padding: 0px !important;

  .next-content {
    gap: 4px;
  }

  &:focus::before {
    background-color: $primary-button-background !important;
  }

  &:hover {
    background-color: #948CFA;
  }

  &:disabled {
    opacity: 0.4;
    background-color: $primary-button-background !important;
  }
}

.theme--light.v-btn.v-btn--disabled.v-btn--has-bg {
  opacity: 0.4;
  background-color: $primary-button-background !important;
  color: white !important;
}

.theme--light.v-btn.v-btn--disabled .v-icon {
  color: white !important;
}

@media (max-width: 600px) {
  .progress-container {
    width: 100%;
    margin: 0 26px 0 21px;

    .progress-bar {
      border-radius: 8px;
    }
  }

  .next {
    width: calc(100vw - 48px);
  }

  .base-container {
    padding-bottom: 42px !important;

    .question-number {
      margin-top: 0px !important;
    }
  }
}

@media (max-width: 767px) {
  .header-content {
    max-width: 120px;
  }
}

@media (min-width: 768px) {
  .header-content {
    max-width: 240px;
  }
}

@media (min-width: 1264px) {
  .header-content {
    max-width: 608px;
  }

  .tooltip-arrow {
    left: 148px !important;
  }

  .button-container {
    gap: 0px;
  }
}

@media (min-width: 601px) {
  #Assessment {
    ::-webkit-scrollbar {
      -webkit-appearance: none;
      width: 7px;
    }

    ::-webkit-scrollbar-thumb {
      border-radius: 4px;
      background-color: rgba(0, 0, 0, 0.5);
      box-shadow: 0 0 1px rgba(255, 255, 255, 0.5);
    }
  }
}
</style>
